import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { ControlledTextField } from "@/uiKit/molecules/ControlledTextField.tsx";

export type ProposeReinclusionModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (nurseMessage: string) => void;
  isPending?: boolean;
};

type ProposeReinclusionFormData = { nurseMessage: string };

const proposeReinclusionFormSchema = z.object({
  // Trimmed before the length check: a whitespace-only reason is no reason.
  nurseMessage: z.string().check(z.trim(), z.minLength(1, "form.required")),
});

export const ProposeReinclusionModal: React.FC<ProposeReinclusionModalProps> = ({
  open,
  onClose,
  onSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const formContext = useForm<ProposeReinclusionFormData>({
    resolver: zodResolver(proposeReinclusionFormSchema),
    defaultValues: { nurseMessage: "" },
  });

  return (
    <Dialog id="propose-reinclusion-dialog" open={open} onClose={onClose} maxWidth="xs">
      <Form formContext={formContext} onSubmit={({ nurseMessage }) => onSubmit(nurseMessage)}>
        <CardHeader
          id="propose-reinclusion-header"
          title={t("reinclusion.proposeModalTitle")}
          onExit={onClose}
          variant="contained"
        />
        <CardContent className={styles.content}>
          <ControlledTextField
            id="propose-reinclusion-nurse-message"
            name="nurseMessage"
            multiline
            placeholder={t("reinclusion.proposeModalPlaceholder")}
          />
        </CardContent>
        <CardFooter className={styles.footer}>
          <Button variant="outlined" data-action="inclusion.reinclusion.cancel" onClick={onClose} disabled={isPending}>
            {t("reinclusion.proposeModalCancel")}
          </Button>
          <Button variant="contained" data-action="inclusion.reinclusion.propose" type="submit" pending={isPending}>
            {t("reinclusion.proposeModalSubmit")}
          </Button>
        </CardFooter>
      </Form>
    </Dialog>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
  }),
  content: css({
    paddingTop: theme.spacing(8),
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(8),
  }),
});
